import createClient, { Middleware } from "openapi-fetch";
import type { paths } from "./schema/order-link-api-schema"; // generated by openapi-typescript

const API_BASE_URL = process.env.REACT_APP_API_URL;

let refreshPromise: Promise<Response> | null = null;

function isTokenExpired(): boolean {
  const cookie = document.cookie
    .split("; ")
    .find(row => row.startsWith("access_token_expiry="));
  if (!cookie) return true; // no cookie → treat as expired

  const encodedValue = cookie.split("=")[1];
  if (!encodedValue) return false;

  const expiryStr = decodeURIComponent(encodedValue);
  const expiryDate = new Date(expiryStr);

  if (isNaN(expiryDate.getTime())) return true; // invalid date → expired

  const now = new Date();
  return expiryDate.getTime() <= now.getTime() + 30000;
}

async function refreshIfExpired() {
  if (!isTokenExpired()) return;

  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE_URL}/v1/api/authentication/refresh`, {
      method: "POST",
      credentials: "include",
    }).finally(() => {
      refreshPromise = null;
    });
  }

  await refreshPromise;
}

const myMiddleware: Middleware = {
  async onRequest({ request }) {
    try {
      await refreshIfExpired(); // refresh token before sending request
    } catch (err) {
      console.error("Failed to refresh token", err);
      return request;
    }
    return request;
  },
  async onResponse({ response }) {
    if (response.status == 401 && response.headers.get("X-Auth-Status") == "TokenExpired") {
      window.location.href = "/"
      return;
    }

    const clonedResponse = response.clone();
    try {
      // Check if response has a body
      const text = await clonedResponse.text();
      if (!text) return response; // empty body → skip conversion

      // Try parsing JSON
      const data = JSON.parse(text);
      const convertedData = traverseAndConvertDates(data);

      return new Response(JSON.stringify(convertedData), {
        ...response,
        headers: response.headers,
        status: response.status,
        statusText: response.statusText,
      });
    } catch (error) {
      console.error("Error processing response for date conversion:", error);
      return response;
    }
  },
};

const convertUTCToLocalTime = (dateString: string): string => {
  let utcDateString = dateString;

  if (!dateString.endsWith("Z")) {
    utcDateString += "Z";
  }

  const utcDate = new Date(utcDateString);

  if (isNaN(utcDate.getTime())) {
    console.error("Invalid Date encountered: ", dateString);
    return dateString; // Return the original string if parsing failed
  }

  return utcDate.toString(); // Convert to local time
};

const isISODateString = (str: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{2,3}$/.test(str);
};

const traverseAndConvertDates = (obj: any): any => {
  if (obj === null) {
    return null; // Explicitly handle `null` values
  } else if (Array.isArray(obj)) {
    return obj.map(traverseAndConvertDates); // Recursively process array elements
  } else if (typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        traverseAndConvertDates(value),
      ])
    ); // Recursively process object entries
  } else if (typeof obj === "string" && isISODateString(obj)) {
    return convertUTCToLocalTime(obj); // Convert ISO string to local time
  }

  return obj;
};

const client = createClient<paths>({
  baseUrl: API_BASE_URL,
  credentials: "include",
});

// Register middleware
client.use(myMiddleware);

export default client;
